import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productAPI } from '../services/api';
import { ProductCard } from '../components/shared/index';

const CATEGORIES = ['Formal','Casual','Party','Traditional','Streetwear','Luxury Evening','Ethnic','Western','Fusion'];
const SIZES      = ['XS','S','M','L','XL','XXL'];
const SORTS      = [['','Default'],['price-asc','Price: Low → High'],['price-desc','Price: High → Low'],['popular','Most Popular']];


// ── Product Card Skeleton ─────────────────────────────────────────────────────
function CardSkeleton({ i = 0 }) {
  const delay = `${i * 0.06}s`;
  const sh = {
    background: 'linear-gradient(90deg, var(--cream-dark) 25%, var(--cream) 50%, var(--cream-dark) 75%)',
    backgroundSize: '200% 100%',
    animation: `shimmer 1.6s ease-in-out infinite, pulse 1.6s ease-in-out infinite`,
    animationDelay: delay,
  };
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', overflow: 'hidden' }}>
      {/* Image placeholder — 3:4 ratio */}
      <div style={{ aspectRatio: '3/4', ...sh }} />
      {/* Text lines */}
      <div style={{ padding: '12px 14px 16px' }}>
        <div style={{ height: 7, width: '45%', borderRadius: 2, marginBottom: 10, ...sh }} />
        <div style={{ height: 13, width: '85%', borderRadius: 2, marginBottom: 8, ...sh }} />
        <div style={{ height: 11, width: '60%', borderRadius: 2, marginBottom: 14, ...sh }} />
        <div style={{ height: 9, width: '40%', borderRadius: 2, ...sh }} />
      </div>
    </div>
  );
}

function BrowseSkeleton({ count = 12 }) {
  return (
    <div className="grid-4 gap-20">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} i={i} />
      ))}
    </div>
  );
}

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [pages,     setPages]     = useState(1);
  const [fuzzyHint, setFuzzyHint] = useState(null);
  const [drawerOpen,setDrawerOpen]= useState(false);

  const [search,   setSearch]   = useState(searchParams.get('search')   || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [size,     setSize]     = useState('');
  const [sort,     setSort]     = useState(searchParams.get('sort')     || '');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  useEffect(() => {
    setSearch(searchParams.get('search') || '');
    setCategory(searchParams.get('category') || '');
    setSort(searchParams.get('sort') || '');
  }, [searchParams]);

  const fetchProducts = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: 12 };
      if (search)   params.search   = search;
      if (category) params.category = category;
      if (size)     params.size     = size;
      if (sort)     params.sort     = sort;
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      const { data } = await productAPI.getAll(params);
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
      setPage(pg);
      if (data.fuzzy && data.suggestedCategory && search) {
        setFuzzyHint({ category: data.suggestedCategory, originalSearch: search });
      } else {
        setFuzzyHint(null);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, category, size, sort, minPrice, maxPrice]);

  useEffect(() => { fetchProducts(1); }, [category, size, sort]); // eslint-disable-line

  // Search: trigger on debounce (500ms) so typing feels live, not just on submit
  useEffect(() => {
    const t = setTimeout(() => { fetchProducts(1); }, 480);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const clearFilters = () => {
    setSearch(''); setCategory(''); setSize(''); setSort(''); setMinPrice(''); setMaxPrice('');
    setSearchParams({});
    setTimeout(() => fetchProducts(1), 50);
  };

  const activeFilterCount = [category, size, sort, minPrice || maxPrice].filter(Boolean).length;

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>

      {/* ── Page Header ── */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '24px clamp(16px,4vw,48px) 0' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>The Collection</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, paddingBottom: 20 }}>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(26px,5vw,44px)', fontStyle: 'italic', fontWeight: 300, margin: 0, lineHeight: 1 }}>
              Browse All Pieces
            </h1>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{total} {total === 1 ? 'piece' : 'pieces'}</span>
          </div>

          {/* ── Desktop filter bar (hidden on mobile) ── */}
          <div className="browse-desktop-filters" style={{ borderTop: '1px solid var(--border)', display: 'flex', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={s.cell}>
              <div style={s.cellLabel}>Search</div>
              <form onSubmit={e => { e.preventDefault(); fetchProducts(1); }} style={{ display: 'flex' }}>
                <input style={s.cellInput} placeholder="Name, style..." value={search} onChange={e => setSearch(e.target.value)} />
                <button type="submit" style={s.goBtn}>Go</button>
              </form>
            </div>
            {/* Category */}
            <div style={s.cell}>
              <div style={s.cellLabel}>Category</div>
              <select style={s.cellSelect} value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">All</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Size */}
            <div style={{ ...s.cell, flex: '2 1 auto' }}>
              <div style={s.cellLabel}>Size</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {SIZES.map(sz => (
                  <button key={sz} onClick={() => setSize(size === sz ? '' : sz)} style={{
                    padding: '4px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                    border: '1px solid ' + (size === sz ? 'var(--ink)' : 'var(--border)'),
                    background: size === sz ? 'var(--ink)' : 'transparent',
                    color: size === sz ? 'var(--cream)' : 'var(--muted)',
                    cursor: 'pointer', transition: 'all 0.12s',
                  }}>{sz}</button>
                ))}
              </div>
            </div>
            {/* Sort */}
            <div style={s.cell}>
              <div style={s.cellLabel}>Sort</div>
              <select style={s.cellSelect} value={sort} onChange={e => setSort(e.target.value)}>
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {/* Price */}
            <div style={s.cell}>
              <div style={s.cellLabel}>Price (₹)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={{ ...s.cellInput, width: 64 }} placeholder="Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} onBlur={() => fetchProducts(1)} />
                <span style={{ color: 'var(--muted)', fontSize: 11 }}>–</span>
                <input style={{ ...s.cellInput, width: 64 }} placeholder="Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} onBlur={() => fetchProducts(1)} />
              </div>
            </div>
            {/* Clear */}
            {activeFilterCount > 0 && (
              <div style={{ ...s.cell, justifyContent: 'center' }}>
                <button onClick={clearFilters} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)' }}>
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile toolbar (hidden on desktop) ── */}
      <div className="browse-mobile-toolbar" style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '0 16px', display: 'none' }}>
        <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--border)' }}>
          {/* Mobile search */}
          <form onSubmit={e => { e.preventDefault(); fetchProducts(1); }} style={{ flex: 1, display: 'flex', borderRight: '1px solid var(--border)' }}>
            <input
              style={{ flex: 1, padding: '12px 12px', fontSize: 13, border: 'none', outline: 'none', background: 'transparent', fontFamily: "'Josefin Sans', sans-serif" }}
              placeholder="Search styles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" style={{ padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--gold)', fontWeight: 700 }}>Go</button>
          </form>
          {/* Filter toggle button */}
          <button
            onClick={() => setDrawerOpen(true)}
            style={{ padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink)', flexShrink: 0 }}
          >
            Filters
            {activeFilterCount > 0 && (
              <span style={{ background: 'var(--gold)', color: 'white', fontSize: 9, fontWeight: 800, padding: '1px 5px', lineHeight: '14px' }}>{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile Filter Drawer ── */}
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(14,11,8,0.5)' }} onClick={() => setDrawerOpen(false)}>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'white', borderRadius: '16px 16px 0 0', maxHeight: '85vh', overflowY: 'auto', padding: '0 0 32px' }} onClick={e => e.stopPropagation()}>
            {/* Drawer handle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--ink)' }}>Filters</div>
              <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--muted)', lineHeight: 1, padding: 4 }}>×</button>
            </div>

            <div style={{ padding: '20px 20px 0' }}>
              {/* Category */}
              <div style={s.drawerSection}>
                <div style={s.drawerLabel}>Category</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  <button onClick={() => setCategory('')} style={{ ...s.drawerChip, ...(category === '' ? s.drawerChipActive : {}) }}>All</button>
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setCategory(c)} style={{ ...s.drawerChip, ...(category === c ? s.drawerChipActive : {}) }}>{c}</button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div style={s.drawerSection}>
                <div style={s.drawerLabel}>Size</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {SIZES.map(sz => (
                    <button key={sz} onClick={() => setSize(size === sz ? '' : sz)} style={{
                      width: 48, height: 48, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
                      border: '1px solid ' + (size === sz ? 'var(--ink)' : 'var(--border)'),
                      background: size === sz ? 'var(--ink)' : 'transparent',
                      color: size === sz ? 'var(--cream)' : 'var(--muted)',
                      cursor: 'pointer',
                    }}>{sz}</button>
                  ))}
                </div>
              </div>

              {/* Sort */}
              <div style={s.drawerSection}>
                <div style={s.drawerLabel}>Sort By</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {SORTS.map(([v, l]) => (
                    <button key={v} onClick={() => setSort(v)} style={{
                      padding: '12px 14px', fontSize: 13, textAlign: 'left', background: sort === v ? 'var(--cream-dark)' : 'transparent',
                      border: 'none', borderLeft: '2px solid ' + (sort === v ? 'var(--gold)' : 'transparent'),
                      cursor: 'pointer', color: sort === v ? 'var(--ink)' : 'var(--muted)', fontWeight: sort === v ? 600 : 400,
                      fontFamily: "'Josefin Sans', sans-serif",
                    }}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div style={s.drawerSection}>
                <div style={s.drawerLabel}>Price Range (₹)</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input style={{ ...s.cellInput, flex: 1 }} placeholder="Min" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
                  <span style={{ color: 'var(--muted)' }}>–</span>
                  <input style={{ ...s.cellInput, flex: 1 }} placeholder="Max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Drawer footer */}
            <div style={{ display: 'flex', gap: 10, padding: '20px 20px 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
              {activeFilterCount > 0 && (
                <button onClick={() => { clearFilters(); setDrawerOpen(false); }} style={{ flex: 1, padding: '13px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', cursor: 'pointer' }}>
                  Clear All
                </button>
              )}
              <button onClick={() => { fetchProducts(1); setDrawerOpen(false); }} style={{ flex: 2, padding: '13px 0', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
                Show Results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(16px,3vw,36px) clamp(16px,4vw,48px)' }}>

        {/* Fuzzy hint */}
        {fuzzyHint && (
          <div style={{ borderLeft: '3px solid var(--gold)', padding: '10px 16px', background: 'white', border: '1px solid var(--border)', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13 }}>
            <span>Showing <strong style={{ color: 'var(--gold)' }}>{fuzzyHint.category}</strong> — closest to "<em>{fuzzyHint.originalSearch}</em>"</span>
            <button onClick={() => setFuzzyHint(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        )}

        {/* Active search banner */}
        {search && !fuzzyHint && (
          <div style={{ borderLeft: '3px solid var(--ink)', padding: '8px 14px', background: 'white', border: '1px solid var(--border)', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
            <span>Results for <strong>"{search}"</strong></span>
            <button onClick={clearFilters} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        )}

        {/* Active filter pills on mobile */}
        {activeFilterCount > 0 && (
          <div className="browse-active-pills" style={{ display: 'none', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {category && <span style={s.pill}>{category} <button onClick={() => setCategory('')} style={s.pillX}>×</button></span>}
            {size     && <span style={s.pill}>{size} <button onClick={() => setSize('')} style={s.pillX}>×</button></span>}
            {sort     && <span style={s.pill}>{SORTS.find(([v]) => v === sort)?.[1]} <button onClick={() => setSort('')} style={s.pillX}>×</button></span>}
            {(minPrice || maxPrice) && <span style={s.pill}>₹{minPrice||'0'} – ₹{maxPrice||'∞'} <button onClick={() => { setMinPrice(''); setMaxPrice(''); }} style={s.pillX}>×</button></span>}
          </div>
        )}

        {/* Grid */}
      {loading ? (
          <BrowseSkeleton count={12} />
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', border: '1px solid var(--border)' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontStyle: 'italic', fontWeight: 300, color: 'var(--cream-dark)', marginBottom: 8 }}>No pieces found</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>Try adjusting your filters</div>
            <button onClick={clearFilters} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', padding: '10px 24px', cursor: 'pointer' }}>
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid-4 gap-20">
              {products.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
            {pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 40, flexWrap: 'wrap' }}>
                {Array.from({ length: pages }, (_, i) => i + 1).map(pg => (
                  <button key={pg} onClick={() => fetchProducts(pg)} style={{
                    width: 36, height: 36, fontSize: 12, fontWeight: 700,
                    border: '1px solid ' + (pg === page ? 'var(--ink)' : 'var(--border)'),
                    background: pg === page ? 'var(--ink)' : 'white',
                    color: pg === page ? 'var(--cream)' : 'var(--muted)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}>{pg}</button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .browse-desktop-filters { display: none !important; }
          .browse-mobile-toolbar  { display: block !important; }
          .browse-active-pills    { display: flex !important; }
        }
      `}</style>
    </div>
  );
}

const s = {
  cell:           { display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 18px', borderRight: '1px solid var(--border)', flex: '1 1 auto' },
  cellLabel:      { fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--muted)' },
  cellInput:      { padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', background: 'white', outline: 'none', fontFamily: "'Josefin Sans', sans-serif", boxSizing: 'border-box' },
  cellSelect:     { padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', background: 'white', outline: 'none', cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif" },
  goBtn:          { padding: '6px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'var(--ink)', color: 'var(--cream)', border: 'none', cursor: 'pointer', flexShrink: 0 },
  drawerSection:  { marginBottom: 24 },
  drawerLabel:    { fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 12 },
  drawerChip:     { padding: '7px 14px', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' },
  drawerChipActive: { border: '1px solid var(--ink)', background: 'var(--ink)', color: 'var(--cream)' },
  pill:           { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', fontSize: 11, background: 'var(--ink)', color: 'var(--cream)', fontWeight: 600 },
  pillX:          { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1, padding: 0 },
};